import { Spinner, SpinnerSize } from "@fluentui/react";
import React, { CSSProperties, useEffect, useState } from "react";
import { selectUserToken } from "../slices/Auth.slice";
import { useAppSelector } from "../store/hooks";

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    flexDirection: "column",
    gap: "20px"
  } as CSSProperties,
  progressContainer: {
    display: "flex",
    gap: "8px",
    justifyContent: "center"
  } as CSSProperties,
  progressDot: (isActive: boolean) =>
    ({
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      backgroundColor: isActive ? "#0078d4" : "#e5e5e5",
      transition: "background-color 0.3s ease"
    }) as CSSProperties
};

export const AppInitializer: React.FC<{
  children: React.ReactNode;
  onInitialize: () => Promise<void>;
}> = ({ children, onInitialize }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStep, setLoadingStep] = useState(0);
  const token = useAppSelector((state) => selectUserToken(state));

  const loadingSteps = [
    "Initializing Security 360...",
    "Loading configuration...",
    "Fetching security data...",
    "Setting up dashboard..."
  ];

  useEffect(() => {
    const initialize = async () => {
      const stepInterval = setInterval(() => {
        setLoadingStep((prev) => (prev < loadingSteps.length - 1 ? prev + 1 : prev));
      }, 800);

      try {
        await onInitialize();
      } catch (error) {
        console.error("Error during app initialization:", error);
      } finally {
        clearInterval(stepInterval);
        setIsLoading(false);
      }
    };

    if (token) {
      initialize();
    } else {
      console.error("No access token available.");
      setIsLoading(false);
    }
  }, [token, onInitialize]);

  if (isLoading) {
    return (
      <div style={styles.container}>
        <Spinner size={SpinnerSize.large} label={loadingSteps[loadingStep]} />
        <div style={styles.progressContainer}>
          {loadingSteps.map((_, index) => (
            <div key={index} style={styles.progressDot(index <= loadingStep)} />
          ))}
        </div>
      </div>
    );
  }

  return children;
};
